import type FullCalendar from '@fullcalendar/react';
import React, {
  useState,
  type FunctionComponent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createContext } from 'use-context-selector';

import type {
  InitialView,
  InitialViewCustomDays,
  ViewId,
} from '@trello/business-logic-react/planner';

import type { UpsellCohort } from './useUpsellData';

export interface PlannerContextValue {
  calendarRef: RefObject<FullCalendar | null>;
  plannerWrapperRef: RefObject<HTMLDivElement>;
  upsellCohort: UpsellCohort;
  currentView: ViewId;
  initialView: InitialView;
  setInitialView: React.Dispatch<React.SetStateAction<InitialView>>;
  initialViewCustomDays: InitialViewCustomDays;
  setInitialViewCustomDays: React.Dispatch<
    React.SetStateAction<InitialViewCustomDays>
  >;
  initialViewShowWeekends: boolean;
  setInitialViewShowWeekends: React.Dispatch<React.SetStateAction<boolean>>;
  mapInitialViewToViewId: (
    view: InitialView,
    customDays: InitialViewCustomDays,
  ) => ViewId;
}

export const emptyPlannerContext: PlannerContextValue = {
  calendarRef: { current: null },
  plannerWrapperRef: { current: null },
  upsellCohort: 'paidUser',
  currentView: 'timeGridWeek',
  initialView: 'flexible',
  initialViewCustomDays: undefined,
  setInitialView: () => {},
  setInitialViewCustomDays: () => {},
  initialViewShowWeekends: true,
  setInitialViewShowWeekends: () => {},
  mapInitialViewToViewId: () => 'timeGridWeek',
};

export const PlannerContext =
  createContext<PlannerContextValue>(emptyPlannerContext);

/**
 * A bit hard to get this right: the FullCalendar component allows us to plug in
 * a ref object to gain access to the underlying FullCalendar instance.
 * This instance is the sanctioned way to access the FullCalendar API, but it's
 * a little awkward because we want to access this API in sibling components of
 * the calendar itself, like the header and width observer.
 *
 * This context provider is connected to the FullCalendar component and should
 * wrap it along with any sibling elements that also need access to the API.
 * However, because of render order, we cannot guarantee that the FullCalendar
 * instance is available on initial render; as a workaround, we _persist_ the
 * instance as a ref object, so consumers can continue to access it on demand.
 *
 * Note that the `getApi()` method simply returns an instance property, so it's
 * extremely lightweight, and consumers need not be concerned about calling it.
 *
 * @example
 * ```ts
 * const { calendarRef } = useContext(PlannerContext);
 *
 * const doSomethingWithCalendarApi = useCallback(() => {
 *   const calendarApi = calendarRef.current?.getApi();
 *   calendarApi?.doSomething();
 * }, [calendarRef]);
 * ```
 */
export const PlannerContextProvider: FunctionComponent<{
  calendarRef: RefObject<FullCalendar | null>;
  plannerWrapperRef: RefObject<HTMLDivElement>;
  upsellCohort: UpsellCohort;
  children: ReactNode;
}> = ({ calendarRef, plannerWrapperRef, upsellCohort, children }) => {
  const [initialView, setInitialView] = useState<InitialView>('flexible');

  const [initialViewCustomDays, setInitialViewCustomDays] =
    useState<InitialViewCustomDays>(undefined);

  const [initialViewShowWeekends, setInitialViewShowWeekends] =
    useState<boolean>(true);

  /**
   * Converts user-friendly view preferences into the specific view identifiers
   * that FullCalendar expects for rendering different calendar layouts.
   *
   * @param view - The initial view type ('custom', 'day', 'week', or 'flexible')
   * @param customDays - For custom views, specifies the number of days (2-6)
   * @returns The corresponding FullCalendar ViewId string
   */
  const mapInitialViewToViewId = (
    view: InitialView,
    customDays: InitialViewCustomDays,
  ): ViewId => {
    if (view === 'day') {
      return 'timeGridDay';
    } else if (view === 'week') {
      return 'timeGridWeek';
    } else if (view === 'custom') {
      if (customDays === '2') {
        return 'timeGridTwoDay';
      } else if (customDays === '3') {
        return 'timeGridThreeDay';
      } else if (customDays === '4') {
        return 'timeGridFourDay';
      } else if (customDays === '5') {
        return 'timeGridFiveDay';
      } else if (customDays === '6') {
        return 'timeGridSixDay';
      }
    }

    return 'timeGridWeek';
  };

  // eslint-disable-next-line @eslint-react/no-unstable-context-value
  const value = {
    calendarRef,
    currentView:
      (calendarRef.current?.getApi()?.view.type as ViewId) || 'timeGridWeek',
    plannerWrapperRef,
    upsellCohort,
    initialView,
    setInitialView,
    initialViewCustomDays,
    setInitialViewCustomDays,
    initialViewShowWeekends,
    setInitialViewShowWeekends,
    mapInitialViewToViewId,
  };

  return (
    <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>
  );
};
